import React, { useState } from "react";
import {
	Trophy,
	Users,
	TrendingUp,
	Calendar,
	Filter,
	Search,
	ChevronDown,
	ChevronUp,
	User,
	Star,
	Award,
	Medal,
	BarChart3,
	Target,
	Clock,
	Settings,
	Plus,
	Edit3,
	Trash2,
} from "lucide-react";
import CustomModal from "./CustomModal";

const TrainerRecords = ({
	allUserRecords = [],
	users = [],
	movements = [],
	countTypes = [],
	userRecords = [],
	onAddMovement,
	onDeleteMovement,
	onAddCountType,
	onDeleteCountType,
	onAddRecord,
	onDeleteRecord,
	loading = false,
}) => {
	const [activeTab, setActiveTab] = useState("overview"); // overview, leaderboards, management, addMovement
	const [selectedUser, setSelectedUser] = useState("all");
	const [selectedMovement, setSelectedMovement] = useState("all");
	const [searchTerm, setSearchTerm] = useState("");
	const [showAddMovement, setShowAddMovement] = useState(false);
	const [showAddCountType, setShowAddCountType] = useState(false);
	const [expandedUsers, setExpandedUsers] = useState(new Set());

	// Form states for adding movements/count types
	const [newMovement, setNewMovement] = useState("");
	const [newCountType, setNewCountType] = useState({
		value: "",
		label: "",
		unit: "",
	});

	// "Record for" user selection - empty string means "Myself"
	const [recordForUser, setRecordForUser] = useState("");
	const [userSearchTerm, setUserSearchTerm] = useState("");
	const [userDropdownOpen, setUserDropdownOpen] = useState(false);

	// Alphabetically sorted and filtered users for the "Record for" dropdown
	const sortedUsers = [...users].sort((a, b) =>
		(a.name || "").localeCompare(b.name || ""),
	);
	const filteredUsers = sortedUsers.filter((u) =>
		(u.name || "").toLowerCase().includes(userSearchTerm.toLowerCase()),
	);

	// Form state for movement recording
	const PLACEHOLDER_MAP = {
		time: "5:30",
		weight: "100",
		reps: "20",
		rounds: "10",
		distance: "5.2",
		calories: "350",
	};

	const defaultMeasurements = () => [
		{ countType: "weight", value: "" },
		{ countType: "reps", value: "" },
	];

	const [trainerRecord, setTrainerRecord] = useState({
		movement: "",
		measurements: defaultMeasurements(),
		notes: "",
		date: new Date().toISOString().split("T")[0],
	});
	const [trainerExpandedMovements, setTrainerExpandedMovements] = useState(
		new Set(),
	);
	const [deleteConfirm, setDeleteConfirm] = useState(null);

	const [modal, setModal] = useState({
		show: false,
		title: "",
		message: "",
		type: "info",
	});

	// Filter records based on selected filters
	const filteredRecords = allUserRecords.filter((record) => {
		const matchesUser =
			selectedUser === "all" || record.userId === selectedUser;
		const matchesMovement =
			selectedMovement === "all" || record.movement === selectedMovement;
		const matchesSearch =
			searchTerm === "" ||
			record.movement.toLowerCase().includes(searchTerm.toLowerCase()) ||
			record.userName.toLowerCase().includes(searchTerm.toLowerCase());
		return matchesUser && matchesMovement && matchesSearch;
	});

	// Group records by user for overview
	const recordsByUser = filteredRecords.reduce((acc, record) => {
		if (!acc[record.userId]) {
			acc[record.userId] = {
				user: users.find((u) => u.uid === record.userId) || {
					name: record.userName,
					uid: record.userId,
				},
				records: [],
			};
		}
		acc[record.userId].records.push(record);
		return acc;
	}, {});

	// Create leaderboards by movement
	const isBetter = (recordA, recordB) => {
		if (recordA.countType === "time") {
			const timeA = parseFloat(recordA.value.replace(":", "."));
			const timeB = parseFloat(recordB.value.replace(":", "."));
			return timeA < timeB;
		} else {
			return parseFloat(recordA.value) > parseFloat(recordB.value);
		}
	};

	const leaderboards = movements.reduce((acc, movement) => {
		const movementRecords = allUserRecords.filter(
			(r) => r.movement === movement,
		);
		if (movementRecords.length === 0) return acc;

		// Group by user and get best record for each
		const userBests = {};
		movementRecords.forEach((record) => {
			const userId = record.userId;
			if (!userBests[userId] || isBetter(record, userBests[userId])) {
				userBests[userId] = record;
			}
		});

		// Sort by performance
		const sortedBests = Object.values(userBests).sort((a, b) => {
			if (a.countType === "time") {
				const timeA = parseFloat(a.value.replace(":", "."));
				const timeB = parseFloat(b.value.replace(":", "."));
				return timeA - timeB; // Lower is better for time
			} else {
				return parseFloat(b.value) - parseFloat(a.value); // Higher is better for others
			}
		});

		acc[movement] = sortedBests.slice(0, 10); // Top 10
		return acc;
	}, {});

	const toggleUserExpanded = (userId) => {
		const newExpanded = new Set(expandedUsers);
		if (newExpanded.has(userId)) {
			newExpanded.delete(userId);
		} else {
			newExpanded.add(userId);
		}
		setExpandedUsers(newExpanded);
	};

	const handleAddMovement = () => {
		if (!newMovement.trim()) {
			setModal({
				show: true,
				title: "Missing Name",
				message: "Please enter a movement name.",
				type: "warning",
			});
			return;
		}
		onAddMovement(newMovement.trim());
		setNewMovement("");
		setShowAddMovement(false);
	};

	const handleAddCountType = () => {
		if (!newCountType.value || !newCountType.label || !newCountType.unit) {
			setModal({
				show: true,
				title: "Missing Fields",
				message: "Please fill in all fields.",
				type: "warning",
			});
			return;
		}
		onAddCountType(newCountType);
		setNewCountType({ value: "", label: "", unit: "" });
		setShowAddCountType(false);
	};

	// Trainer's own movement recording handlers
	const updateTrainerMeasurement = (index, field, val) => {
		setTrainerRecord((prev) => ({
			...prev,
			measurements: prev.measurements.map((m, i) =>
				i === index ? { ...m, [field]: val } : m,
			),
		}));
	};

	const addTrainerMeasurement = () => {
		setTrainerRecord((prev) => ({
			...prev,
			measurements: [...prev.measurements, { countType: "", value: "" }],
		}));
	};

	const removeTrainerMeasurement = (index) => {
		setTrainerRecord((prev) => ({
			...prev,
			measurements: prev.measurements.filter((_, i) => i !== index),
		}));
	};

	const handleAddTrainerRecord = async (e) => {
		e.preventDefault();
		const movement = trainerRecord.movement;
		const validMeasurements = trainerRecord.measurements.filter(
			(m) => m.countType && m.value,
		);
		if (!movement || validMeasurements.length === 0) {
			setModal({
				show: true,
				title: "Missing Fields",
				message: "Please select a movement and add at least one measurement.",
				type: "warning",
			});
			return;
		}
		const measurements = validMeasurements.map((m) => {
			const info = countTypes.find((ct) => ct.value === m.countType);
			return {
				countType: m.countType,
				countTypeLabel: info.label,
				countUnit: info.unit,
				value: m.value.trim(),
			};
		});
		const primary = measurements[0];
		const record = {
			movement: movement.trim(),
			countType: primary.countType,
			countTypeLabel: primary.countTypeLabel,
			countUnit: primary.countUnit,
			value: primary.value,
			measurements,
			notes: trainerRecord.notes.trim(),
			date: trainerRecord.date,
		};

		// Build options for recording on behalf of another user
		const options = {};
		if (recordForUser) {
			const targetUser = users.find((u) => u.uid === recordForUser);
			if (targetUser) {
				options.targetUserId = targetUser.uid;
				options.targetUserName = targetUser.name;
			}
		}

		try {
			await onAddRecord(record, options);
			setTrainerRecord({
				movement: "",
				measurements: defaultMeasurements(),
				notes: "",
				date: new Date().toISOString().split("T")[0],
			});
			const targetName = recordForUser
				? users.find((u) => u.uid === recordForUser)?.name || "user"
				: null;
			setModal({
				show: true,
				title: "Movement Saved",
				message: targetName
					? `Movement has been recorded for ${targetName}.`
					: "Your movement has been recorded.",
				type: "success",
			});
		} catch (error) {
			setModal({
				show: true,
				title: "Error",
				message: error.message || "Failed to save movement.",
				type: "error",
			});
		}
	};

	const toggleTrainerMovementExpanded = (movement) => {
		const newExpanded = new Set(trainerExpandedMovements);
		if (newExpanded.has(movement)) {
			newExpanded.delete(movement);
		} else {
			newExpanded.add(movement);
		}
		setTrainerExpandedMovements(newExpanded);
	};

	// Group records by movement for the selected user (or trainer's own)
	const displayRecords = recordForUser
		? allUserRecords.filter((r) => r.userId === recordForUser)
		: userRecords;

	const trainerGroupedRecords = displayRecords.reduce((acc, record) => {
		if (!acc[record.movement]) acc[record.movement] = [];
		acc[record.movement].push(record);
		return acc;
	}, {});

	// Sort each group by date descending
	Object.keys(trainerGroupedRecords).forEach((movement) => {
		trainerGroupedRecords[movement].sort(
			(a, b) => new Date(b.date) - new Date(a.date),
		);
	});

	const trainerSelectedTypes = trainerRecord.measurements
		.map((m) => m.countType)
		.filter(Boolean);

	const getTrainerMeasurements = (record) => {
		if (record.measurements && record.measurements.length > 0) {
			return record.measurements;
		}
		return [
			{
				countType: record.countType,
				countTypeLabel: record.countTypeLabel,
				countUnit: record.countUnit,
				value: record.value,
			},
		];
	};

	const formatTrainerMeasurements = (record) => {
		return getTrainerMeasurements(record)
			.map((m) => `${m.value} ${m.countUnit}`)
			.join(" × ");
	};

	const getRecordStats = () => {
		const totalRecords = allUserRecords.length;
		const activeUsers = new Set(allUserRecords.map((r) => r.userId)).size;
		const totalMovements = new Set(allUserRecords.map((r) => r.movement)).size;
		const thisMonth = allUserRecords.filter((r) => {
			const recordDate = new Date(r.date);
			const now = new Date();
			return (
				recordDate.getMonth() === now.getMonth() &&
				recordDate.getFullYear() === now.getFullYear()
			);
		}).length;

		return { totalRecords, activeUsers, totalMovements, thisMonth };
	};

	const stats = getRecordStats();

	return (
		<div>
			{/* Header with Tabs (desktop) / Dropdown (mobile) */}
			<div className="card" style={{ overflow: "visible" }}>
				{/* Desktop tabs */}
				<div className="card-header d-none d-md-block">
					<ul className="nav nav-tabs card-header-tabs">
						<li className="nav-item">
							<button
								className={`nav-link ${activeTab === "overview" ? "active" : ""}`}
								onClick={() => setActiveTab("overview")}
								style={{
									border: "none",
									background: "none",
									color: activeTab === "overview" ? "#d15a36" : "#fff",
									fontWeight: activeTab === "overview" ? 600 : 400,
								}}
							>
								<BarChart3 size={16} className="me-1" />
								Overview
							</button>
						</li>
						<li className="nav-item">
							<button
								className={`nav-link ${activeTab === "leaderboards" ? "active" : ""}`}
								onClick={() => setActiveTab("leaderboards")}
								style={{
									border: "none",
									background: "none",
									color: activeTab === "leaderboards" ? "#d15a36" : "#fff",
									fontWeight: activeTab === "leaderboards" ? 600 : 400,
								}}
							>
								<Trophy size={16} className="me-1" />
								Leaderboards
							</button>
						</li>
						<li className="nav-item">
							<button
								className={`nav-link ${activeTab === "management" ? "active" : ""}`}
								onClick={() => setActiveTab("management")}
								style={{
									border: "none",
									background: "none",
									color: activeTab === "management" ? "#d15a36" : "#fff",
									fontWeight: activeTab === "management" ? 600 : 400,
								}}
							>
								<Settings size={16} className="me-1" />
								Movement Management
							</button>
						</li>
						<li className="nav-item">
							<button
								className={`nav-link ${activeTab === "addMovement" ? "active" : ""}`}
								onClick={() => setActiveTab("addMovement")}
								style={{
									border: "none",
									background: "none",
									color: activeTab === "addMovement" ? "#d15a36" : "#fff",
									fontWeight: activeTab === "addMovement" ? 600 : 400,
								}}
							>
								<Plus size={16} className="me-1" />
								Add Movement
							</button>
						</li>
					</ul>
				</div>

				{/* Mobile dropdown */}
				<div className="card-header d-md-none p-3">
					<select
						className="form-select"
						value={activeTab}
						onChange={(e) => setActiveTab(e.target.value)}
						style={{ fontSize: "16px" }}
					>
						<option value="overview">Overview</option>
						<option value="leaderboards">Leaderboards</option>
						<option value="management">Movement Management</option>
						<option value="addMovement">Add Movement</option>
					</select>
				</div>

				<div
					className="card-body"
					style={{ overflow: "visible", padding: "1rem" }}
				>
					{/* Stats Grid */}
					<div
						style={{
							display: "grid",
							gridTemplateColumns: "1fr 1fr",
							gap: "0.5rem",
							marginBottom: "1rem",
						}}
					>
						<div className="bg-primary bg-opacity-10 rounded p-2 text-center">
							<Target size={18} className="text-primary mb-1" />
							<h5 className="mb-0">{stats.totalRecords}</h5>
							<small className="text-muted" style={{ fontSize: "0.7rem" }}>
								Total Movements
							</small>
						</div>
						<div className="bg-success bg-opacity-10 rounded p-2 text-center">
							<Users size={18} className="text-success mb-1" />
							<h5 className="mb-0">{stats.activeUsers}</h5>
							<small className="text-muted" style={{ fontSize: "0.7rem" }}>
								Active Users
							</small>
						</div>
						<div className="bg-info bg-opacity-10 rounded p-2 text-center">
							<Target size={18} className="text-info mb-1" />
							<h5 className="mb-0">{stats.totalMovements}</h5>
							<small className="text-muted" style={{ fontSize: "0.7rem" }}>
								Movements Tracked
							</small>
						</div>
						<div className="bg-warning bg-opacity-10 rounded p-2 text-center">
							<TrendingUp size={18} className="text-warning mb-1" />
							<h5 className="mb-0">{stats.thisMonth}</h5>
							<small className="text-muted" style={{ fontSize: "0.7rem" }}>
								This Month
							</small>
						</div>
					</div>

					{/* Tab Content */}
					{activeTab === "overview" && (
						<div>
							{/* Filters */}
							<div
								className="mb-4"
								style={{
									display: "flex",
									flexDirection: "column",
									gap: "0.75rem",
								}}
							>
								<div>
									<label className="form-label">Filter by User</label>
									<select
										className="form-select"
										value={selectedUser}
										onChange={(e) => setSelectedUser(e.target.value)}
									>
										<option value="all">All Users</option>
										{users.map((user) => (
											<option key={user.uid} value={user.uid}>
												{user.name}
											</option>
										))}
									</select>
								</div>
								<div>
									<label className="form-label">Filter by Movement</label>
									<select
										className="form-select"
										value={selectedMovement}
										onChange={(e) => setSelectedMovement(e.target.value)}
									>
										<option value="all">All Movements</option>
										{movements.map((movement) => (
											<option key={movement} value={movement}>
												{movement}
											</option>
										))}
									</select>
								</div>
								<div>
									<label className="form-label">Search</label>
									<div className="input-group">
										<span className="input-group-text">
											<Search size={16} />
										</span>
										<input
											type="text"
											className="form-control"
											placeholder="Search users or movements..."
											value={searchTerm}
											onChange={(e) => setSearchTerm(e.target.value)}
										/>
									</div>
								</div>
							</div>

							{/* User Records */}
							<div>
								<h6 className="mb-3">
									User Movements ({Object.keys(recordsByUser).length} users)
								</h6>
								{Object.keys(recordsByUser).length === 0 ? (
									<div className="text-center py-4">
										<p className="text-muted">
											No movements found with current filters.
										</p>
									</div>
								) : (
									<div
										style={{
											display: "flex",
											flexDirection: "column",
											gap: "0.75rem",
										}}
									>
										{Object.entries(recordsByUser).map(
											([userId, { user, records }]) => {
												const isExpanded = expandedUsers.has(userId);
												const recordCount = records.length;
												const uniqueMovements = new Set(
													records.map((r) => r.movement),
												).size;

												return (
													<div key={userId}>
														<div
															className="border rounded"
															style={{ overflow: "visible" }}
														>
															<div
																className="p-3 bg-light border-bottom"
																onClick={() => toggleUserExpanded(userId)}
																style={{ cursor: "pointer" }}
															>
																<div className="d-flex justify-content-between align-items-center">
																	<div
																		className="d-flex align-items-center"
																		style={{ minWidth: 0, flex: 1 }}
																	>
																		<div
																			className="dashboard-user-avatar me-2 me-md-3 flex-shrink-0"
																			style={{
																				width: "36px",
																				height: "36px",
																				fontSize: "14px",
																			}}
																		>
																			{user.name.charAt(0).toUpperCase()}
																		</div>
																		<div style={{ minWidth: 0 }}>
																			<h6 className="mb-0 text-truncate">
																				{user.name}
																			</h6>
																			<small className="text-muted">
																				{recordCount} records •{" "}
																				{uniqueMovements} movements
																			</small>
																		</div>
																	</div>
																	<div className="d-flex align-items-center flex-shrink-0 ms-2">
																		<span className="badge bg-primary me-1 d-none d-sm-inline">
																			{recordCount} entries
																		</span>
																		{isExpanded ? (
																			<ChevronUp size={20} />
																		) : (
																			<ChevronDown size={20} />
																		)}
																	</div>
																</div>
															</div>

															{isExpanded && (
																<div className="p-2 p-md-3">
																	{/* Desktop table */}
																	<div className="d-none d-md-block">
																		<div className="table-responsive">
																			<table className="table table-sm table-hover mb-0">
																				<thead>
																					<tr>
																						<th>Movement</th>
																						<th>Best Value</th>
																						<th>Type</th>
																						<th>Date</th>
																						<th>Notes</th>
																					</tr>
																				</thead>
																				<tbody>
																					{records.map((record) => (
																						<tr key={record.id}>
																							<td className="fw-bold">
																								{record.movement}
																							</td>
																							<td>
																								<span className="badge bg-success">
																									{record.measurements &&
																									record.measurements.length > 0
																										? record.measurements
																												.map(
																													(m) =>
																														`${m.value} ${m.countUnit}`,
																												)
																												.join(" × ")
																										: `${record.value} ${record.countUnit}`}
																								</span>
																							</td>
																							<td>
																								<small
																									style={{ color: "#000" }}
																								>
																									{record.measurements &&
																									record.measurements.length > 0
																										? record.measurements
																												.map(
																													(m) =>
																														m.countTypeLabel,
																												)
																												.join(", ")
																										: record.countTypeLabel}
																								</small>
																							</td>
																							<td>
																								<small>
																									{new Date(
																										record.date,
																									).toLocaleDateString()}
																								</small>
																							</td>
																							<td>
																								<small
																									style={{ color: "#000" }}
																								>
																									{record.notes || "-"}
																								</small>
																							</td>
																						</tr>
																					))}
																				</tbody>
																			</table>
																		</div>
																	</div>

																	{/* Mobile stacked cards */}
																	<div className="d-md-none">
																		{records.map((record) => (
																			<div
																				key={record.id}
																				className="mb-2 p-2"
																				style={{
																					backgroundColor: "#1a1a1a",
																					borderRadius: "8px",
																					border: "1px solid #333",
																				}}
																			>
																				<div
																					style={{
																						fontWeight: 600,
																						color: "#fff",
																						marginBottom: "4px",
																					}}
																				>
																					{record.movement}
																				</div>
																				<div className="d-flex flex-wrap align-items-center gap-2">
																					<span
																						className="badge bg-success"
																						style={{ fontSize: "0.75rem" }}
																					>
																						{record.measurements &&
																						record.measurements.length > 0
																							? record.measurements
																									.map(
																										(m) =>
																											`${m.value} ${m.countUnit}`,
																									)
																									.join(" × ")
																							: `${record.value} ${record.countUnit}`}
																					</span>
																					<small
																						style={{
																							color: "rgba(255,255,255,0.5)",
																						}}
																					>
																						{record.measurements &&
																						record.measurements.length > 0
																							? record.measurements
																									.map((m) => m.countTypeLabel)
																									.join(", ")
																							: record.countTypeLabel}
																					</small>
																					<small
																						style={{
																							color: "rgba(255,255,255,0.5)",
																						}}
																					>
																						{new Date(
																							record.date,
																						).toLocaleDateString()}
																					</small>
																				</div>
																				{record.notes && (
																					<div
																						style={{
																							fontSize: "0.8rem",
																							color: "rgba(255,255,255,0.4)",
																							fontStyle: "italic",
																							marginTop: "4px",
																						}}
																					>
																						{record.notes}
																					</div>
																				)}
																			</div>
																		))}
																	</div>
																</div>
															)}
														</div>
													</div>
												);
											},
										)}
									</div>
								)}
							</div>
						</div>
					)}

					{activeTab === "leaderboards" && (
						<div>
							<h6 className="mb-3">Movement Leaderboards</h6>
							{Object.keys(leaderboards).length === 0 ? (
								<div className="text-center py-4">
									<Trophy size={48} className="text-muted mb-3" />
									<p className="text-muted">
										No movements recorded for leaderboards yet.
									</p>
								</div>
							) : (
								<div className="row g-4">
									{Object.entries(leaderboards).map(([movement, records]) => (
										<div key={movement} className="col-12 col-lg-6">
											<div className="card h-100">
												<div className="card-header bg-primary bg-opacity-10">
													<h6 className="mb-0 d-flex align-items-center">
														<Medal size={18} className="me-2 text-primary" />
														{movement}
													</h6>
												</div>
												<div className="card-body p-0">
													{records.length === 0 ? (
														<p className="text-muted text-center py-3">
															No movements recorded yet
														</p>
													) : (
														<div className="list-group list-group-flush">
															{records.map((record, index) => (
																<div
																	key={record.id}
																	className="list-group-item d-flex justify-content-between align-items-center"
																>
																	<div className="d-flex align-items-center">
																		<span
																			className={`badge me-3 ${
																				index === 0
																					? "bg-warning"
																					: index === 1
																						? "bg-secondary"
																						: index === 2
																							? "bg-danger"
																							: "bg-light text-dark"
																			}`}
																		>
																			#{index + 1}
																		</span>
																		<div>
																			<div className="fw-bold">
																				{record.userName}
																			</div>
																			<small className="text-muted">
																				{new Date(
																					record.date,
																				).toLocaleDateString()}
																			</small>
																		</div>
																	</div>
																	<div className="text-end">
																		<div className="fw-bold text-success">
																			{record.measurements &&
																			record.measurements.length > 0
																				? record.measurements
																						.map(
																							(m) =>
																								`${m.value} ${m.countUnit}`,
																						)
																						.join(" × ")
																				: `${record.value} ${record.countUnit}`}
																		</div>
																		<small className="text-muted">
																			{record.measurements &&
																			record.measurements.length > 0
																				? record.measurements
																						.map((m) => m.countTypeLabel)
																						.join(", ")
																				: record.countTypeLabel}
																		</small>
																	</div>
																</div>
															))}
														</div>
													)}
												</div>
											</div>
										</div>
									))}
								</div>
							)}
						</div>
					)}

					{activeTab === "management" && (
						<div>
							<div className="row g-4">
								{/* Movement Management */}
								<div className="col-12 col-lg-6">
									<div className="card">
										<div className="card-header d-flex justify-content-between align-items-center">
											<h6 className="mb-0">Movement Management</h6>
											<button
												className="btn btn-primary btn-sm"
												onClick={() => setShowAddMovement(!showAddMovement)}
											>
												{showAddMovement ? (
													"Cancel"
												) : (
													<>
														<Plus size={16} className="me-1" />
														Add Movement
													</>
												)}
											</button>
										</div>
										<div className="card-body">
											{showAddMovement && (
												<div className="mb-3 p-3 bg-light rounded movement-form-mobile">
													<div className="row g-2">
														<div className="col-8">
															<input
																type="text"
																className="form-control"
																placeholder="Movement name (e.g., Turkish Get-up)"
																value={newMovement}
																onChange={(e) => setNewMovement(e.target.value)}
															/>
														</div>
														<div className="col-4">
															<button
																className="btn btn-success w-100"
																onClick={handleAddMovement}
																disabled={loading}
															>
																Add
															</button>
														</div>
													</div>
												</div>
											)}

											<div style={{ maxHeight: "300px", overflowY: "auto" }}>
												{movements.map((movement) => (
													<div
														key={movement}
														className="d-flex justify-content-between align-items-center py-2 border-bottom"
													>
														<span>{movement}</span>
														<button
															className="btn btn-outline-danger btn-sm"
															onClick={() => onDeleteMovement(movement)}
															disabled={loading}
														>
															<Trash2 size={14} />
														</button>
													</div>
												))}
											</div>
										</div>
									</div>
								</div>

								{/* Count Type Management */}
								<div className="col-12 col-lg-6">
									<div className="card">
										<div className="card-header d-flex justify-content-between align-items-center">
											<h6 className="mb-0">Movement Type Management</h6>
											<button
												className="btn btn-primary btn-sm"
												onClick={() => setShowAddCountType(!showAddCountType)}
											>
												{showAddCountType ? (
													"Cancel"
												) : (
													<>
														<Plus size={16} className="me-1" />
														Add Type
													</>
												)}
											</button>
										</div>
										<div className="card-body">
											{showAddCountType && (
												<div className="mb-3 p-3 bg-light rounded movement-form-mobile">
													<div className="row g-2">
														<div className="col-12 col-sm-4">
															<input
																type="text"
																className="form-control form-control-sm"
																placeholder="Value (e.g., speed)"
																value={newCountType.value}
																onChange={(e) =>
																	setNewCountType({
																		...newCountType,
																		value: e.target.value,
																	})
																}
															/>
														</div>
														<div className="col-12 col-sm-4">
															<input
																type="text"
																className="form-control form-control-sm"
																placeholder="Label (e.g., Speed)"
																value={newCountType.label}
																onChange={(e) =>
																	setNewCountType({
																		...newCountType,
																		label: e.target.value,
																	})
																}
															/>
														</div>
														<div className="col-12 col-sm-4">
															<input
																type="text"
																className="form-control form-control-sm"
																placeholder="Unit (e.g., mph)"
																value={newCountType.unit}
																onChange={(e) =>
																	setNewCountType({
																		...newCountType,
																		unit: e.target.value,
																	})
																}
															/>
														</div>
														<div className="col-12">
															<button
																className="btn btn-success btn-sm w-100"
																onClick={handleAddCountType}
																disabled={loading}
															>
																Add Movement Type
															</button>
														</div>
													</div>
												</div>
											)}

											<div style={{ maxHeight: "300px", overflowY: "auto" }}>
												{countTypes.map((type) => (
													<div
														key={type.value}
														className="d-flex justify-content-between align-items-center py-2 border-bottom"
													>
														<div>
															<span className="fw-bold">{type.label}</span>
															<small className="text-muted ms-2">
																({type.unit})
															</small>
														</div>
														<button
															className="btn btn-outline-danger btn-sm"
															onClick={() => onDeleteCountType(type.value)}
															disabled={loading}
														>
															<Trash2 size={14} />
														</button>
													</div>
												))}
											</div>
										</div>
									</div>
								</div>
							</div>
						</div>
					)}

					{/* Add Movement Tab - Trainer's own movement tracking */}
					{activeTab === "addMovement" && (
						<div>
							{/* Add Movement Form */}
							<div className="card mb-4">
								<div className="card-header">
									<h6 className="mb-0">
										<Plus size={16} className="me-2" />
										Record a Movement
									</h6>
								</div>
								<div className="card-body">
									<form onSubmit={handleAddTrainerRecord}>
										<div className="row g-3">
											{/* Record for user dropdown with search */}
											<div className="col-12">
												<label className="form-label">Record for</label>
												<div style={{ position: "relative" }}>
													<div
														className="form-select d-flex align-items-center justify-content-between"
														onClick={() =>
															!loading && setUserDropdownOpen(!userDropdownOpen)
														}
														style={{
															cursor: loading ? "not-allowed" : "pointer",
															userSelect: "none",
														}}
													>
														<span>
															{recordForUser
																? users.find((u) => u.uid === recordForUser)
																		?.name || "Select user"
																: "Myself"}
														</span>
														<ChevronDown size={16} style={{ opacity: 0.6 }} />
													</div>
													{userDropdownOpen && (
														<div
															style={{
																position: "absolute",
																top: "100%",
																left: 0,
																right: 0,
																zIndex: 1050,
																background: "#2d2d2d",
																border: "1px solid #444",
																borderRadius: "0.375rem",
																marginTop: "2px",
																maxHeight: "250px",
																display: "flex",
																flexDirection: "column",
																boxShadow: "0 4px 12px rgba(0,0,0,0.3)",
															}}
														>
															<div
																style={{
																	padding: "0.5rem",
																	borderBottom: "1px solid #444",
																}}
															>
																<input
																	type="text"
																	className="form-control form-control-sm"
																	placeholder="Search users..."
																	value={userSearchTerm}
																	onChange={(e) =>
																		setUserSearchTerm(e.target.value)
																	}
																	autoFocus
																	onClick={(e) => e.stopPropagation()}
																/>
															</div>
															<div style={{ overflowY: "auto", flex: 1 }}>
																<div
																	className="px-3 py-2"
																	style={{
																		cursor: "pointer",
																		background:
																			recordForUser === ""
																				? "#d15a36"
																				: "transparent",
																		color: "#fff",
																	}}
																	onClick={() => {
																		setRecordForUser("");
																		setUserSearchTerm("");
																		setUserDropdownOpen(false);
																	}}
																>
																	Myself
																</div>
																{filteredUsers.map((u) => (
																	<div
																		key={u.uid}
																		className="px-3 py-2"
																		style={{
																			cursor: "pointer",
																			background:
																				recordForUser === u.uid
																					? "#d15a36"
																					: "transparent",
																			color: "#fff",
																		}}
																		onMouseEnter={(e) => {
																			if (recordForUser !== u.uid)
																				e.currentTarget.style.background =
																					"#3a3a3a";
																		}}
																		onMouseLeave={(e) => {
																			if (recordForUser !== u.uid)
																				e.currentTarget.style.background =
																					"transparent";
																		}}
																		onClick={() => {
																			setRecordForUser(u.uid);
																			setUserSearchTerm("");
																			setUserDropdownOpen(false);
																		}}
																	>
																		{u.name}
																	</div>
																))}
																{filteredUsers.length === 0 && (
																	<div
																		className="px-3 py-2 text-muted"
																		style={{ fontStyle: "italic" }}
																	>
																		No users found
																	</div>
																)}
															</div>
														</div>
													)}
													{/* Backdrop to close dropdown when clicking outside */}
													{userDropdownOpen && (
														<div
															style={{
																position: "fixed",
																top: 0,
																left: 0,
																right: 0,
																bottom: 0,
																zIndex: 1049,
															}}
															onClick={() => {
																setUserDropdownOpen(false);
																setUserSearchTerm("");
															}}
														/>
													)}
												</div>
											</div>
											<div className="col-12 col-md-6">
												<label className="form-label">Movement *</label>
												<select
													className="form-select"
													value={trainerRecord.movement}
													onChange={(e) =>
														setTrainerRecord({
															...trainerRecord,
															movement: e.target.value,
														})
													}
													disabled={loading}
												>
													<option value="">Select movement</option>
													{movements.map((m) => (
														<option key={m} value={m}>
															{m}
														</option>
													))}
												</select>
											</div>
											<div className="col-6 col-md-3">
												<label className="form-label">Date</label>
												<input
													type="date"
													className="form-control"
													value={trainerRecord.date}
													onChange={(e) =>
														setTrainerRecord({
															...trainerRecord,
															date: e.target.value,
														})
													}
													disabled={loading}
												/>
											</div>
											<div className="col-6 col-md-3">
												<label className="form-label">Notes</label>
												<input
													type="text"
													className="form-control"
													value={trainerRecord.notes}
													onChange={(e) =>
														setTrainerRecord({
															...trainerRecord,
															notes: e.target.value,
														})
													}
													placeholder="Optional notes"
													disabled={loading}
												/>
											</div>

											{/* Measurements */}
											<div className="col-12">
												<label className="form-label">Measurements *</label>
												{trainerRecord.measurements.map((m, index) => (
													<div
														key={index}
														className="row g-2 mb-2 align-items-center"
													>
														<div className="col-5">
															<select
																className="form-select form-select-sm"
																value={m.countType}
																onChange={(e) =>
																	updateTrainerMeasurement(
																		index,
																		"countType",
																		e.target.value,
																	)
																}
																disabled={loading}
															>
																<option value="">Select type</option>
																{countTypes
																	.filter(
																		(ct) =>
																			ct.value === m.countType ||
																			!trainerSelectedTypes.includes(ct.value),
																	)
																	.map((ct) => (
																		<option key={ct.value} value={ct.value}>
																			{ct.label} ({ct.unit})
																		</option>
																	))}
															</select>
														</div>
														<div className="col-5">
															<input
																type="text"
																className="form-control form-control-sm"
																value={m.value}
																onChange={(e) =>
																	updateTrainerMeasurement(
																		index,
																		"value",
																		e.target.value,
																	)
																}
																placeholder={
																	PLACEHOLDER_MAP[m.countType] || "Value"
																}
																disabled={loading}
															/>
														</div>
														<div className="col-2">
															{trainerRecord.measurements.length > 1 && (
																<button
																	type="button"
																	className="btn btn-outline-danger btn-sm"
																	onClick={() =>
																		removeTrainerMeasurement(index)
																	}
																	disabled={loading}
																>
																	<Trash2 size={14} />
																</button>
															)}
														</div>
													</div>
												))}
												{trainerRecord.measurements.length <
													countTypes.length && (
													<button
														type="button"
														className="btn btn-outline-secondary btn-sm"
														onClick={addTrainerMeasurement}
														disabled={loading}
													>
														<Plus size={14} className="me-1" />
														Add Measurement
													</button>
												)}
											</div>

											<div className="col-12">
												<button
													type="submit"
													className="btn btn-success me-2"
													disabled={loading}
												>
													{loading ? "Saving..." : "Save Movement"}
												</button>
											</div>
										</div>
									</form>
								</div>
							</div>

							{/* Trainer's Own Movement History */}
							<div className="card">
								<div className="card-header">
									<h6 className="mb-0">
										<TrendingUp size={16} className="me-2" />
										{recordForUser
											? `${users.find((u) => u.uid === recordForUser)?.name || "User"}'s Movement History`
											: "Your Movement History"}
									</h6>
								</div>
								<div className="card-body">
									{Object.keys(trainerGroupedRecords).length === 0 ? (
										<div className="text-center py-4">
											<p className="text-muted">
												{recordForUser
													? `No movements recorded for ${users.find((u) => u.uid === recordForUser)?.name || "this user"} yet.`
													: "No movements recorded yet. Start tracking above!"}
											</p>
										</div>
									) : (
										<div className="list-group">
											{Object.entries(trainerGroupedRecords).map(
												([movement, records]) => {
													const isExpanded =
														trainerExpandedMovements.has(movement);
													return (
														<div
															key={movement}
															className="list-group-item mb-2"
															style={{ borderRadius: "8px" }}
														>
															<div
																className="d-flex justify-content-between align-items-center"
																onClick={() =>
																	toggleTrainerMovementExpanded(movement)
																}
																style={{ cursor: "pointer" }}
															>
																<div>
																	<h6 className="mb-0">{movement}</h6>
																	<small className="text-muted">
																		{records.length}{" "}
																		{records.length === 1 ? "entry" : "entries"}
																	</small>
																</div>
																<div className="d-flex align-items-center gap-2">
																	<span className="badge bg-success">
																		{formatTrainerMeasurements(records[0])}
																	</span>
																	{isExpanded ? (
																		<ChevronUp size={20} />
																	) : (
																		<ChevronDown size={20} />
																	)}
																</div>
															</div>

															{isExpanded && (
																<div className="mt-3">
																	{/* Desktop table */}
																	<div className="table-responsive d-none d-md-block">
																		<table className="table table-sm table-hover mb-0">
																			<thead>
																				<tr>
																					<th>Date</th>
																					<th>Value</th>
																					<th>Notes</th>
																					<th></th>
																				</tr>
																			</thead>
																			<tbody>
																				{records.map((record) => (
																					<tr key={record.id}>
																						<td>
																							{new Date(
																								record.date,
																							).toLocaleDateString()}
																						</td>
																						<td>
																							<span className="badge bg-success">
																								{formatTrainerMeasurements(
																									record,
																								)}
																							</span>
																						</td>
																						<td>
																							<small className="text-muted">
																								{record.notes || "—"}
																							</small>
																						</td>
																						<td>
																							{deleteConfirm === record.id ? (
																								<div className="d-flex gap-1">
																									<button
																										className="btn btn-danger btn-sm"
																										onClick={() => {
																											onDeleteRecord(record.id);
																											setDeleteConfirm(null);
																										}}
																										disabled={loading}
																									>
																										Confirm
																									</button>
																									<button
																										className="btn btn-secondary btn-sm"
																										onClick={() =>
																											setDeleteConfirm(null)
																										}
																									>
																										Cancel
																									</button>
																								</div>
																							) : (
																								<button
																									className="btn btn-outline-danger btn-sm"
																									onClick={() =>
																										setDeleteConfirm(record.id)
																									}
																									disabled={loading}
																								>
																									<Trash2 size={14} />
																								</button>
																							)}
																						</td>
																					</tr>
																				))}
																			</tbody>
																		</table>
																	</div>

																	{/* Mobile card layout */}
																	<div className="d-md-none">
																		{records.map((record) => (
																			<div
																				key={record.id}
																				className="mb-2 p-2"
																				style={{
																					backgroundColor: "#1a1a1a",
																					borderRadius: "8px",
																					border: "1px solid #333",
																				}}
																			>
																				<div className="d-flex justify-content-between align-items-center mb-1">
																					<span
																						style={{
																							color: "rgba(255,255,255,0.6)",
																							fontSize: "0.85rem",
																						}}
																					>
																						{new Date(
																							record.date,
																						).toLocaleDateString()}
																					</span>
																					<span
																						className="badge bg-success"
																						style={{ fontSize: "0.75rem" }}
																					>
																						{formatTrainerMeasurements(record)}
																					</span>
																				</div>
																				{record.notes && (
																					<div
																						style={{
																							fontSize: "0.8rem",
																							color: "rgba(255,255,255,0.45)",
																							fontStyle: "italic",
																							marginBottom: "0.5rem",
																						}}
																					>
																						{record.notes}
																					</div>
																				)}
																				<div className="d-flex justify-content-end">
																					{deleteConfirm === record.id ? (
																						<div className="d-flex gap-1">
																							<button
																								className="btn btn-danger btn-sm"
																								onClick={() => {
																									onDeleteRecord(record.id);
																									setDeleteConfirm(null);
																								}}
																								disabled={loading}
																								style={{ fontSize: "0.8rem" }}
																							>
																								Confirm
																							</button>
																							<button
																								className="btn btn-secondary btn-sm"
																								onClick={() =>
																									setDeleteConfirm(null)
																								}
																								style={{ fontSize: "0.8rem" }}
																							>
																								Cancel
																							</button>
																						</div>
																					) : (
																						<button
																							className="btn btn-outline-danger btn-sm"
																							onClick={() =>
																								setDeleteConfirm(record.id)
																							}
																							disabled={loading}
																						>
																							<Trash2 size={14} />
																						</button>
																					)}
																				</div>
																			</div>
																		))}
																	</div>
																</div>
															)}
														</div>
													);
												},
											)}
										</div>
									)}
								</div>
							</div>
						</div>
					)}
				</div>
			</div>
			<CustomModal
				show={modal.show}
				title={modal.title}
				message={modal.message}
				type={modal.type}
				onClose={() => setModal({ ...modal, show: false })}
			/>
		</div>
	);
};

export default TrainerRecords;
